import { useEffect, useRef } from "react";
import { DateTime } from "luxon";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useAdminRouteV2ControllerGetDateOfLiveRoutes,
  useAdminRouteV2ControllerGetDateOfBuildRoutes,
} from "service";

/**
 * Hook to handle initial navigation to Route Planner.
 * Priority order:
 * 1. Navigate to live date if it exists
 * 2. If no live date, navigate to planned date
 * 3. If neither exists, navigate to current day
 *
 * This hook only runs once on initial mount and only when the user
 * navigates from outside the route planner (not when changing dates within).
 */
export const useInitialRoutePlannerDate = (
  currentDateParam: string | undefined,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Fetch live and planned route dates
  const { data: liveRouteData, isLoading: isLoadingLive } =
    useAdminRouteV2ControllerGetDateOfLiveRoutes({});
  const { data: plannedRouteData, isLoading: isLoadingPlanned } =
    useAdminRouteV2ControllerGetDateOfBuildRoutes({});

  useEffect(() => {
    // Only redirect once per navigation to route planner from outside
    if (hasRedirected.current) return;

    // Wait for data to load
    if (isLoadingLive || isLoadingPlanned) return;

    // Check if we came from outside route planner
    // We use the state to track if this is an internal navigation
    const isInternalNavigation = location.state?.internalNavigation === true;
    if (isInternalNavigation) {
      hasRedirected.current = true;
      return;
    }

    const shouldResolveInitialDate =
      location.state?.resolveRoutePlannerDate === true;
    const parsedCurrentDate = currentDateParam
      ? DateTime.fromFormat(currentDateParam, "MM-dd-yyyy")
      : null;

    // A valid URL date is intentional unless navigation explicitly asks us to
    // resolve the default route-planner date (live > planned > today).
    if (parsedCurrentDate?.isValid && !shouldResolveInitialDate) {
      hasRedirected.current = true;
      return;
    }

    // Determine the target date based on priority
    let targetDate: DateTime | null = null;

    // Priority 1: Live date
    if (liveRouteData?.data?.date) {
      targetDate = DateTime.fromISO(
        liveRouteData.data.date as unknown as string,
      );
    }
    // Priority 2: Planned date
    else if (plannedRouteData?.data?.date) {
      targetDate = DateTime.fromISO(
        plannedRouteData.data.date as unknown as string,
      );
    }
    // Priority 3: Current day
    else {
      targetDate = DateTime.now();
    }

    // Format the target date
    const targetDateFormatted = targetDate.toFormat("MM-dd-yyyy");

    // Only redirect if the target date is different from the current URL date
    if (currentDateParam !== targetDateFormatted) {
      hasRedirected.current = true;
      navigate(`/route-planner/${targetDateFormatted}`, {
        replace: true,
        state: { internalNavigation: true },
      });
    } else {
      // Even if we don't redirect, mark as done so we don't keep checking
      hasRedirected.current = true;
    }
  }, [
    liveRouteData,
    plannedRouteData,
    isLoadingLive,
    isLoadingPlanned,
    currentDateParam,
    navigate,
    location.state,
  ]);

  // Reset the redirect flag when unmounting (leaving route planner)
  useEffect(() => {
    return () => {
      hasRedirected.current = false;
    };
  }, []);

  // Check if we're still determining where to redirect
  const isInternalNavigation = location.state?.internalNavigation === true;
  const isRedirecting =
    !hasRedirected.current &&
    !isInternalNavigation &&
    (isLoadingLive || isLoadingPlanned);

  return {
    isLoading: isLoadingLive || isLoadingPlanned,
    isRedirecting,
    hasRedirected: hasRedirected.current,
    liveDate: liveRouteData?.data?.date,
    plannedDate: plannedRouteData?.data?.date,
  };
};
